import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { TID } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FilePdf, Brain, Target, CheckCircle, WarningCircle, ArrowRight, ChartLineUp, Scales, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import { useAnalysis } from "../contexts/AnalysisContext";

export default function DealPricing() {
  const [form, setForm] = useState({
    business_name: "", industry: "", state: "", time_in_business_months: "",
    credit_score: "", requested_amount: "", requested_term_months: "", product_type: "",
    monthly_revenue: "", avg_daily_balance: "", negative_days: "", nsfs: "", notes: "",
  });
  const [files, setFilesLocal] = useState([]);
  const analysis = useAnalysis();
  const { uploaded, parsed, recs, status: analysisStatus, phase, elapsed, isRunning } = analysis;
  const setParsed = analysis.setParsed;
  const setRecs = analysis.setRecs;
  const setFiles = (next) => { setFilesLocal(next); analysis.setFiles(next); };
  const busy = { upload: analysisStatus === "uploading", analyze: analysisStatus === "analyzing", recommend: false, save: false };
  const [busyMeta, setBusyMeta] = useState({ recommend: false });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const parsedRef = React.useRef(null);
  const reportRef = React.useRef(null);

  // Elapsed/phase/status come from the global AnalysisContext so they survive
  // navigation. When parsed metrics arrive, mirror form-fields into local form state.
  React.useEffect(() => {
    if (!parsed || parsed.error) return;
    const patch = {};
    if (parsed.avg_monthly_revenue != null) patch.monthly_revenue = String(Math.round(parsed.avg_monthly_revenue));
    const adb = parsed.avg_daily_ending_balance ?? parsed.avg_daily_balance;
    if (adb != null) patch.avg_daily_balance = String(Math.round(adb));
    if (parsed.negative_days != null) patch.negative_days = String(parsed.negative_days);
    if (parsed.nsfs != null) patch.nsfs = String(parsed.nsfs);
    if (parsed.industry_inferred) patch.industry = (cur) => cur || parsed.industry_inferred;
    if (parsed.business_name) patch.business_name = (cur) => cur || parsed.business_name;
    if (parsed.max_loan_recommendation?.max_loan_high > 0) {
      const mid = Math.round((parsed.max_loan_recommendation.max_loan_low + parsed.max_loan_recommendation.max_loan_high) / 2);
      patch.requested_amount = (cur) => cur || String(mid);
    }
    setForm((f) => {
      const next = { ...f };
      for (const [k, v] of Object.entries(patch)) {
        next[k] = typeof v === "function" ? v(f[k]) : v;
      }
      return next;
    });
    setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [parsed]);

  const buildDealPayload = () => {
    const numerics = ["time_in_business_months","credit_score","requested_amount","requested_term_months","monthly_revenue","avg_daily_balance","negative_days","nsfs"];
    const payload = { ...form };
    numerics.forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
    payload.bank_statement_file_ids = uploaded.map((u) => u.id);
    if (parsed && !parsed.error) {
      payload.parsed_metrics = parsed;
      payload.risk_score = parsed.risk_score;
    }
    return payload;
  };

  const handleUploadAndAnalyze = async () => {
    if (!files.length) return toast.error("Pick 1–6 bank statement files first.");
    // Fire-and-forget — analysis runs in the global context so navigating away
    // doesn't pause it. Multitask freely.
    analysis.startAnalysis(files, { form });
  };

  const handleRecommend = async () => {
    setBusyMeta((b) => ({ ...b, recommend: true }));
    try {
      const { data } = await api.post("/deals/recommend", buildDealPayload());
      setRecs(data.recommendations || []);
      toast.success(`${(data.recommendations || []).length} lender matches`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recommendation failed");
    } finally { setBusyMeta((b) => ({ ...b, recommend: false })); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Pricing</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter">Price a new deal</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">Upload 3–6 months of bank statements, review the AI analysis, then run a ranked lender match with confidence scoring.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Deal form (2 cols) */}
        <div className="lg:col-span-2 space-y-6" data-testid={TID.dealForm}>
          <Card title="Bank statements" step="1" subtitle="Upload 3–6 months of PDF statements. AI extracts revenue, MCAs, NSFs, and computes your max loan in 30–60 seconds.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <FF label="Business name (optional)"><Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Acme LLC — for saving / your records" data-testid="deal-business-name" /></FF>
              <FF label="State (optional)"><Input value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0,2))} placeholder="NY" data-testid={TID.dealState} /></FF>
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
              <FilePdf size={32} weight="duotone" className="mx-auto text-slate-400" />
              <div className="text-sm text-slate-600 mt-2">PDF, PNG or JPG — up to 6 files</div>
              <input
                data-testid={TID.bankUpload}
                type="file"
                multiple
                accept="application/pdf,image/*"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="mt-3 text-sm mx-auto"
              />
              {files.length > 0 && (
                <ul className="mt-3 text-xs text-slate-700 font-mono space-y-0.5">
                  {files.map((f, i) => (<li key={i}>• {f.name} ({Math.round(f.size / 1024)} KB)</li>))}
                </ul>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={handleUploadAndAnalyze} disabled={busy.upload || busy.analyze || !files.length} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.bankAnalyze}>
                <Brain size={14} className="mr-1.5" />
                {busy.upload ? "Uploading…" : busy.analyze ? "Analyzing with AI…" : "Upload & Analyze with AI"}
              </Button>
              {uploaded.length > 0 && !busy.analyze && (
                <span className="self-center text-xs text-emerald-700 font-mono">✓ {uploaded.length} uploaded</span>
              )}
            </div>

            {/* Big prominent progress card with circular timer */}
            {(busy.upload || busy.analyze) && (() => {
              // Realistic estimate: ~20s base + ~15s per file (text extraction + Gemini)
              const fileCount = Math.max(1, files.length);
              const expected = 20 + 15 * fileCount;
              const overrun = elapsed > expected;
              return (
                <div className="mt-4 rounded-lg border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-emerald-50 p-5" data-testid="analyze-progress">
                  <div className="flex items-center gap-5">
                    <ProgressRing elapsed={elapsed} expected={expected} />
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-base font-bold text-slate-900">{phase || "Working…"}</div>
                      <div className="text-xs text-slate-600 mt-1">
                        {!overrun && <>~<span className="font-mono font-semibold">{Math.max(1, expected - elapsed)}s</span> remaining</>}
                        {overrun && elapsed < expected * 1.5 && <span className="text-amber-700">Wrapping up — large statements take a bit longer.</span>}
                        {elapsed >= expected * 1.5 && <span className="text-amber-700">Still working. Big or scanned PDFs need extra time — we won't bail.</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-2 font-mono">
                        Elapsed {elapsed}s · {fileCount} file{fileCount !== 1 ? "s" : ""} · est. {expected}s
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {parsed && parsed.error && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                <strong>Analysis failed:</strong> {parsed.error}{parsed.detail ? ` — ${parsed.detail}` : ""}. Try fewer / clearer PDF statements.
              </div>
            )}
          </Card>

          {/* Anchor for auto-scroll once analysis completes */}
          <div ref={reportRef} />

          {parsed && !parsed.error && (
            <Card title="Litigation check" step="⚖️" subtitle="Always check NY courts before funding. We copy the business name and open the official search pages for you.">
              {form.business_name ? (
                <CourtCheck businessName={form.business_name} />
              ) : (
                <div className="space-y-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                    <strong>Add a business name</strong> to enable one-click NYSCEF / eCourts / PACER searches.
                  </div>
                  <Input
                    placeholder="e.g. Acme Trucking LLC"
                    value={form.business_name}
                    onChange={(e) => set("business_name", e.target.value)}
                    data-testid="litigation-business-name"
                    className="max-w-md"
                  />
                </div>
              )}
            </Card>
          )}

          {parsed && !parsed.error && parsed.max_loan_recommendation && (
            <Card title="Max loan recommendation" step="📊" subtitle="The number you came here for — adjust the holdback % to see how the range changes.">
              <MaxLoanCalculator parsed={parsed} />
            </Card>
          )}

          {parsed && !parsed.error && (
            <Card title="MCA stack detection" step="🚨" subtitle={`${parsed.mca_position_count ?? 0} active position(s) detected`}>
              {(parsed.mca_position_count ?? 0) === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm text-emerald-800">
                  <strong>✓ Clean stack</strong> — no existing MCA positions detected. Full underwriting capacity available.
                </div>
              ) : (
                <>
                  <div className={`rounded-md p-4 text-sm border mb-4 ${
                    parsed.mca_position_count >= 3 ? "bg-red-50 border-red-200 text-red-900" :
                    parsed.mca_position_count === 2 ? "bg-amber-50 border-amber-200 text-amber-900" :
                    "bg-blue-50 border-blue-200 text-blue-900"
                  }`}>
                    <strong>{parsed.mca_position_count} active MCA position{parsed.mca_position_count !== 1 ? "s" : ""}</strong> detected ·
                    Total daily outflow: <span className="font-mono font-semibold">${Number(parsed.total_daily_mca_outflow ?? 0).toFixed(2)}/day</span> ·
                    Monthly equivalent: <span className="font-mono font-semibold">${Number((parsed.total_daily_mca_outflow ?? 0) * 22).toLocaleString(undefined,{maximumFractionDigits:0})}/mo</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                      <tr>
                        <th className="text-left py-2 font-semibold">Funder / Creditor</th>
                        <th className="text-left py-2 font-semibold">Cadence</th>
                        <th className="text-right py-2 font-semibold">Daily $</th>
                        <th className="text-right py-2 font-semibold">First seen</th>
                        <th className="text-left py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(parsed.existing_mca_payments || []).map((m, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-2 font-semibold">{m.creditor || "Unknown"}</td>
                          <td className="py-2 text-xs text-slate-600">{m.frequency || "—"}</td>
                          <td className="py-2 font-mono text-xs text-right">{m.daily_amount != null ? `$${Number(m.daily_amount).toFixed(2)}` : "—"}</td>
                          <td className="py-2 font-mono text-xs text-right">{m.first_seen || "—"}</td>
                          <td className="py-2 text-xs">
                            {m.still_active === false ? (
                              <span className="inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Paid off</span>
                            ) : (
                              <span className="inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Active</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </Card>
          )}

          {parsed && !parsed.error && (
            <Card title="Parsed bank metrics" step="📈" subtitle={parsed.summary} _ref={parsedRef}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Metric label="Months analyzed" value={parsed.months_analyzed} />
                <Metric label="Avg monthly revenue" value={parsed.avg_monthly_revenue ? `$${Number(parsed.avg_monthly_revenue).toLocaleString()}` : "—"} />
                <Metric label="Avg daily ending bal" value={(parsed.avg_daily_ending_balance ?? parsed.avg_daily_balance) ? `$${Number(parsed.avg_daily_ending_balance ?? parsed.avg_daily_balance).toLocaleString()}` : "—"} />
                <Metric label="Negative days" value={parsed.negative_days ?? "—"} />
                <Metric label="NSFs" value={parsed.nsfs ?? "—"} />
                <Metric label="Industry (inferred)" value={parsed.industry_inferred || "—"} />
                <Metric label="Revenue trend" value={parsed.revenue_trend || "—"} />
                <Metric label="Risk score" value={parsed.risk_score != null ? `${parsed.risk_score}` : "—"} />
              </div>
            </Card>
          )}

          {parsed && !parsed.error && (parsed.monthly_deposits || parsed.ending_balances) && (
            <Card title="Statement analysis" step="📉" subtitle="Monthly deposits, ending balances, and detected loan payments.">
              <BankCharts parsed={parsed} />
            </Card>
          )}

          <Card title="Actions" step="🎯" subtitle="Re-run lender match with current values.">
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleRecommend} disabled={busyMeta.recommend} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.dealRunRecommend}>
                <Target size={14} className="mr-1.5" />{busyMeta.recommend ? "Matching…" : "Run lender match"}
              </Button>
            </div>
          </Card>

          {/* Saved scenarios removed per user request */}
        </div>

        {/* Right: Recommendations */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-300 font-semibold">Recommended</div>
                  <div className="font-heading text-lg font-bold">Best-fit lenders</div>
                </div>
                <Target size={20} weight="duotone" />
              </div>
              <div className="divide-y divide-slate-200 max-h-[80vh] overflow-y-auto">
                {!recs && <div className="px-5 py-8 text-sm text-slate-500 text-center">Run match to see ranked lenders.</div>}
                {recs && recs.length === 0 && <div className="px-5 py-8 text-sm text-slate-500 text-center">No lenders match this deal profile.</div>}
                {recs && groupByTier(recs).map(({ tier, rows }) => (
                  <div key={tier || "untiered"}>
                    <div className="sticky top-0 z-10 bg-slate-100 px-5 py-2 border-y border-slate-200 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center justify-between">
                      <span>{tier || "Untiered"}</span>
                      <span className="text-slate-500 font-mono normal-case tracking-normal text-[10px]">{rows.length} match{rows.length !== 1 ? "es" : ""}</span>
                    </div>
                    {rows.map((r) => (
                      <div key={r.lender_id} data-testid={TID.recRow(r.lender_id)} className="px-5 py-4 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link to={`/lenders/${r.lender_id}`} className="font-semibold text-slate-900 hover:text-blue-600 truncate">{r.lender_name}</Link>
                              <StatusBadge status={r.status} />
                            </div>
                            {r.tiers && r.tiers.length > 1 && (
                              <div className="text-[10px] text-slate-500 mt-1 font-mono uppercase tracking-[0.1em]">
                                also in: {r.tiers.filter((t) => t !== tier).join(" · ")}
                              </div>
                            )}
                            <div className="text-xs text-slate-500 mt-0.5">{r.speed_to_fund}</div>
                          </div>
                          <ConfidencePill v={r.confidence} />
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
                          <div className="text-slate-600">Funding</div>
                          <div className="text-slate-800">{r.estimated_funding_low ? `$${Math.round(r.estimated_funding_low).toLocaleString()}` : "—"}–{r.estimated_funding_high ? `$${Math.round(r.estimated_funding_high).toLocaleString()}` : "—"}</div>
                          <div className="text-slate-600">Term</div>
                          <div className="text-slate-800">{r.estimated_term_low ?? "—"}–{r.estimated_term_high ?? "—"} mo</div>
                        </div>
                        {r.matches?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {r.matches.slice(0, 3).map((m, i) => (<div key={i} className="text-[11px] text-emerald-700 flex items-start gap-1"><CheckCircle size={11} weight="fill" className="mt-0.5 shrink-0" />{m}</div>))}
                          </div>
                        )}
                        {r.issues?.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {r.issues.slice(0, 2).map((m, i) => (<div key={i} className="text-[11px] text-amber-700 flex items-start gap-1"><WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0" />{m}</div>))}
                          </div>
                        )}
                        {r.pricing_notes && <div className="mt-2 text-[11px] text-slate-500 italic">{r.pricing_notes}</div>}
                        {r.red_flags && (
                          <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-md">
                            <span className="font-semibold uppercase tracking-wider text-[9px]">Red flag</span> · {r.red_flags}
                          </div>
                        )}
                        {r.lender_notes && (
                          <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-md">
                            <span className="font-semibold uppercase tracking-wider text-[9px]">From past deals</span> · {r.lender_notes}
                          </div>
                        )}
                        <Link to={`/lenders/${r.lender_id}`} className="mt-2 inline-flex items-center text-[11px] font-medium text-blue-600 hover:text-blue-800">
                          Open profile <ArrowRight size={11} className="ml-0.5" />
                        </Link>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Card = ({ title, subtitle, step, children, _ref }) => (
  <div ref={_ref} className="bg-white border border-slate-200 rounded-lg p-6">
    <div className="flex items-start gap-3 mb-4">
      {step && <div className="h-7 w-7 rounded-md bg-slate-900 text-white grid place-items-center text-xs font-bold">{step}</div>}
      <div>
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
    {children}
  </div>
);
const FF = ({ label, children, full }) => (
  <div className={full ? "md:col-span-3" : ""}>
    <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const Metric = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
    <div className="font-heading text-lg font-bold text-slate-900 mt-0.5 truncate">{value}</div>
  </div>
);
const ConfidencePill = ({ v }) => {  const n = Number(v) || 0;
  const cls = n >= 70 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : n >= 40 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200";
  return <div className={`text-xs font-mono font-semibold px-2 py-0.5 rounded border ${cls} shrink-0`}>{n.toFixed(0)}%</div>;
};

/**
 * Editable max-loan calculator. Mirrors the backend's `compute_max_loan`
 * formula client-side, but with a user-adjustable holdback %. Lets a broker
 * see how aggressive (15-25%) or conservative (5-8%) holdbacks shift the
 * dollar range, without re-running the AI parser.
 */
function MaxLoanCalculator({ parsed }) {
  const m = parsed.max_loan_recommendation || {};
  const initialHoldback = Math.round(((m.holdback_pct_used ?? 0.10) * 100) * 10) / 10;  // e.g. 10.0
  const [holdback, setHoldback] = React.useState(initialHoldback);

  const dailyMca = Number(m.daily_mca_outflow ?? 0);
  const mcaCount = Number(m.existing_mca_count ?? 0);
  const monthlyRevenue = Number(parsed.avg_monthly_revenue ?? 0);
  const dailyRev = monthlyRevenue / 22;
  const pct = (Number(holdback) || 0) / 100;
  const availableDaily = dailyRev * pct - dailyMca;

  // Mirror backend formula
  let maxLow = 0, maxHigh = 0, reasoning = "";
  if (monthlyRevenue <= 0) {
    reasoning = "Insufficient revenue to underwrite a new position.";
  } else if (availableDaily <= 0) {
    reasoning = "No payback capacity left — existing MCAs already consume the available holdback at this %.";
  } else {
    maxLow = Math.round(availableDaily * 100 / 1.45);
    maxHigh = Math.round(availableDaily * 130 / 1.30);
    if (mcaCount >= 3) { maxLow = Math.round(maxLow * 0.5); maxHigh = Math.round(maxHigh * 0.5); }
    else if (mcaCount === 2) { maxLow = Math.round(maxLow * 0.75); maxHigh = Math.round(maxHigh * 0.75); }
    reasoning = `Based on ${holdback}% holdback of $${monthlyRevenue.toLocaleString(undefined,{maximumFractionDigits:0})}/mo revenue ($${dailyRev.toFixed(0)}/day × ${holdback}% = $${(dailyRev*pct).toFixed(0)}/day capacity) minus $${dailyMca.toFixed(2)}/day in ${mcaCount} existing MCA position(s). ${
      mcaCount >= 3 ? "Heavy stack — 50% penalty applied."
      : mcaCount === 2 ? "2nd-position penalty applied (75%)."
      : mcaCount === 1 ? "1 existing MCA — capacity reduced by outflow."
      : "Clean stack — full capacity available."
    }`;
  }

  const hasCapacity = maxHigh > 0;
  const isCustom = Math.abs(holdback - initialHoldback) > 0.01;

  return (
    <div className={`rounded-lg p-5 ${hasCapacity ? "bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
      {/* Holdback slider */}
      <div className="mb-5 bg-white/70 backdrop-blur rounded-md p-3 border border-slate-200/80">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">
            Holdback %
            {isCustom && <span className="ml-2 text-blue-600 normal-case tracking-normal font-mono text-[11px]">(custom)</span>}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.5"
              min="0"
              max="100"
              value={holdback}
              onChange={(e) => setHoldback(Number(e.target.value))}
              className="w-20 h-7 px-2 text-sm font-mono font-semibold border border-slate-300 rounded text-right focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none"
              data-testid="holdback-input"
            />
            <span className="text-sm font-mono font-bold text-slate-900">%</span>
            {isCustom && (
              <button
                onClick={() => setHoldback(initialHoldback)}
                className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 hover:text-slate-900 ml-1"
                data-testid="holdback-reset"
              >
                reset
              </button>
            )}
          </div>
        </div>
        <input
          type="range"
          min="2"
          max="30"
          step="0.5"
          value={holdback}
          onChange={(e) => setHoldback(Number(e.target.value))}
          className="w-full accent-slate-900 cursor-pointer"
          data-testid="holdback-slider"
        />
        <div className="flex justify-between text-[9px] uppercase tracking-[0.15em] font-semibold text-slate-400 mt-1">
          <span>2% (very conservative)</span>
          <span>10% (standard)</span>
          <span>30% (aggressive)</span>
        </div>
      </div>

      <div className="flex items-baseline gap-3 mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">Recommended range</div>
      </div>
      <div className="font-heading text-4xl font-bold tracking-tighter text-slate-900">
        {hasCapacity ? (
          <>${maxLow.toLocaleString()} <span className="text-slate-400 text-2xl">–</span> ${maxHigh.toLocaleString()}</>
        ) : (
          <span className="text-red-700">No capacity</span>
        )}
      </div>
      <div className="mt-3 text-sm text-slate-700">{reasoning}</div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <KV label="Daily capacity" value={`$${availableDaily.toFixed(2)}`} />
        <KV label="MCAs detected" value={mcaCount} />
        <KV label="Daily MCA outflow" value={`$${dailyMca.toFixed(2)}`} />
        <KV label="Holdback applied" value={`${holdback}%`} />
      </div>
    </div>
  );
}

/**
 * Court-check widget — auto-fires a litigation search via CourtListener
 * (free public API; covers federal + state appellate courts) and displays
 * results inline. NY state Supreme/Civil is Cloudflare-protected so we also
 * surface deep-links for manual verification of state-court cases.
 */
function CourtCheck({ businessName }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!businessName || businessName.trim().length < 2) return;
    let alive = true;
    setLoading(true); setErr(null); setData(null);
    api.get("/court-search", { params: { business_name: businessName } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Search failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [businessName]);

  const openWithCopy = async (url, label) => {
    try { await navigator.clipboard.writeText(businessName); } catch {}
    window.open(url, "_blank", "noopener");
    toast.success(`Copied "${businessName}" — paste it in ${label}`);
  };

  const cl = data?.courtlistener;
  const hits = cl?.results || [];
  const cnt = cl?.count || 0;

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-700">
        Searching for cases against <strong className="text-slate-900">{businessName}</strong>
      </div>

      {loading && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-600 flex items-center gap-3">
          <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          Searching CourtListener (federal + state appellate)…
        </div>
      )}

      {!loading && err && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
          Search failed: {err}
        </div>
      )}

      {!loading && data && (
        <>
          <div className={`rounded-md p-4 text-sm border ${cnt > 0 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`} data-testid="court-results-banner">
            {cnt === 0 && <><strong>✓ No federal cases found</strong> for "{businessName}" in CourtListener. Still verify NY state courts below before funding.</>}
            {cnt > 0 && (
              <>
                <strong>⚠ {cnt} case{cnt !== 1 ? "s" : ""} found</strong> mentioning "{businessName}" in federal / state appellate courts.
                Review the top {hits.length} below, plus run NY state checks via the deep-links.
              </>
            )}
          </div>

          {hits.length > 0 && (
            <div className="divide-y divide-slate-200 border border-slate-200 rounded-md overflow-hidden bg-white">
              {hits.map((h, i) => (
                <a
                  key={i}
                  href={h.absolute_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`court-hit-${i}`}
                >
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-semibold text-slate-900 truncate">{h.case_name || "(no case name)"}</div>
                    <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 font-mono">
                      {h.date_filed || "no date"} · {h.court || "—"}
                    </div>
                  </div>
                  {h.docket_number && <div className="text-[11px] text-slate-500 mt-1 font-mono">Docket: {h.docket_number}</div>}
                  {h.snippet && <div className="text-xs text-slate-600 mt-1 italic line-clamp-2" dangerouslySetInnerHTML={{ __html: h.snippet }} />}
                </a>
              ))}
            </div>
          )}
        </>
      )}

      <div className="pt-2 border-t border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">Also check NY state courts manually</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <button onClick={() => openWithCopy("https://iapps.courts.state.ny.us/nyscef/CaseSearch?TAB=name", "Business/Organization name field")} data-testid="court-check-nyscef" className="text-left rounded-md border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors p-3">
            <div className="flex items-center gap-1.5 mb-1"><Scales size={14} weight="duotone" className="text-slate-700" /><span className="font-semibold text-sm">NYSCEF (Supreme)</span></div>
            <div className="text-[11px] text-slate-500">e-filed Supreme Court cases statewide</div>
            <div className="text-[10px] text-blue-600 mt-1 inline-flex items-center gap-1 font-semibold uppercase tracking-[0.1em]"><Copy size={10} /> Copy & open <ArrowSquareOut size={10} /></div>
          </button>
          <button onClick={() => openWithCopy("https://iapps.courts.state.ny.us/webcivilLocal/LCMain", "Business Name field")} data-testid="court-check-ecourts" className="text-left rounded-md border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors p-3">
            <div className="flex items-center gap-1.5 mb-1"><Scales size={14} weight="duotone" className="text-slate-700" /><span className="font-semibold text-sm">eCourts (Civil)</span></div>
            <div className="text-[11px] text-slate-500">NY Civil / City / District courts</div>
            <div className="text-[10px] text-blue-600 mt-1 inline-flex items-center gap-1 font-semibold uppercase tracking-[0.1em]"><Copy size={10} /> Copy & open <ArrowSquareOut size={10} /></div>
          </button>
          <button onClick={() => openWithCopy("https://pacer.uscourts.gov/find-case/search-courts", "PACER party name field")} data-testid="court-check-pacer" className="text-left rounded-md border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors p-3">
            <div className="flex items-center gap-1.5 mb-1"><Scales size={14} weight="duotone" className="text-slate-700" /><span className="font-semibold text-sm">PACER (Federal)</span></div>
            <div className="text-[11px] text-slate-500">Full federal docket detail (deep dive)</div>
            <div className="text-[10px] text-blue-600 mt-1 inline-flex items-center gap-1 font-semibold uppercase tracking-[0.1em]"><Copy size={10} /> Copy & open <ArrowSquareOut size={10} /></div>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Circular SVG progress ring. Fills based on elapsed/expected, capped at 95%
 * (we never claim 100% until we actually have a result, to avoid lying).
 */
const ProgressRing = ({ elapsed, expected = 50, size = 72, stroke = 6 }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const rawPct = Math.min(0.95, elapsed / Math.max(1, expected));
  const overrun = elapsed > expected;
  // After expected time, slowly drift towards 95% with a logarithmic curve so it
  // keeps moving but never finishes.
  const pct = overrun
    ? Math.min(0.95, 0.85 + 0.10 * Math.log10(1 + (elapsed - expected) / 30))
    : rawPct;
  const offset = circumference * (1 - pct);
  const seconds = Math.max(0, expected - elapsed);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* track */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke="#dbeafe" strokeWidth={stroke} fill="none"
        />
        {/* progress arc */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke={overrun ? "#f59e0b" : "#2563eb"}
          strokeWidth={stroke} fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center leading-none">
          <div className={`font-heading text-lg font-bold tracking-tight ${overrun ? "text-amber-700" : "text-slate-900"}`}>
            {overrun ? `${elapsed}s` : `${seconds}s`}
          </div>
          <div className="text-[8px] uppercase tracking-[0.15em] font-semibold text-slate-500 mt-0.5">
            {overrun ? "elapsed" : "left"}
          </div>
        </div>
      </div>
    </div>
  );
};

const TIER_ORDER = ["Tier 1", "Tier 2A", "Tier 2B", "Tier 3A", "Tier 3B", "Tier 4", "Reverse", "Canada", "SBA / MAYA"];
function groupByTier(rows) {
  const byTier = {};
  for (const r of rows) {
    const t = r.tier || "Untiered";
    if (!byTier[t]) byTier[t] = [];
    byTier[t].push(r);
  }
  // Server already sorts within-tier by confidence; respect that order.
  const ordered = [...TIER_ORDER, "Untiered"].filter((t) => byTier[t]);
  return ordered.map((tier) => ({ tier, rows: byTier[tier] }));
}

function BankCharts({ parsed }) {
  const depositRows = Object.entries(parsed.monthly_deposits || {}).sort(([a], [b]) => a.localeCompare(b)).map(([month, amount]) => ({ month, amount: Number(amount) || 0 }));
  const balanceRows = Object.entries(parsed.ending_balances || {}).sort(([a], [b]) => a.localeCompare(b)).map(([month, amount]) => ({ month, amount: Number(amount) || 0 }));
  const loans = parsed.existing_loan_payments || [];
  const largeDeposits = parsed.large_deposits || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {depositRows.length > 0 && (
          <ChartFrame title="Monthly deposits" icon={ChartLineUp}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={depositRows} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="amount" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        )}
        {balanceRows.length > 0 && (
          <ChartFrame title="Ending balances">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={balanceRows} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0" }} />
                <Line type="monotone" dataKey="amount" stroke="#020617" strokeWidth={2} dot={{ r: 4, fill: "#020617" }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loans.length > 0 && (
          <ChartFrame title="Detected loan payments">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                <tr><th className="text-left py-2 font-semibold">Creditor</th><th className="text-left py-2 font-semibold">Cadence</th><th className="text-right py-2 font-semibold">Amount</th></tr>
              </thead>
              <tbody>
                {loans.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2">{l.creditor || "Unknown"}</td>
                    <td className="py-2 text-xs text-slate-500">{l.frequency || "—"}</td>
                    <td className="py-2 font-mono text-xs text-right">{l.weekly_or_monthly != null ? `$${Number(l.weekly_or_monthly).toLocaleString()}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartFrame>
        )}
        {largeDeposits.length > 0 && (
          <ChartFrame title="Large / unusual deposits">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                <tr><th className="text-left py-2 font-semibold">Date</th><th className="text-left py-2 font-semibold">Description</th><th className="text-right py-2 font-semibold">Amount</th></tr>
              </thead>
              <tbody>
                {largeDeposits.slice(0, 8).map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2 font-mono text-xs">{l.date || "—"}</td>
                    <td className="py-2 text-xs text-slate-600">{l.description || "—"}</td>
                    <td className="py-2 font-mono text-xs text-right">{l.amount != null ? `$${Number(l.amount).toLocaleString()}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartFrame>
        )}
      </div>
    </div>
  );
}

const ChartFrame = ({ title, children }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">{title}</div>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div className="bg-white/60 rounded-md px-3 py-2 border border-slate-200">
    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
    <div className="font-mono text-sm font-semibold text-slate-900 mt-0.5 truncate">{value}</div>
  </div>
);
